import Konva from 'konva';
import * as state from '../state.js';
import { attachInteractive, pointerPos } from './_helpers.js';

// Highlight = a transparent rect bordered with the current color, drawn over a
// dim scrim so the highlighted region appears to "pop" against everything else.
// The legacy Snap implementation rendered the scrim with an SVG opacity overlay
// + cut-out paths; in Konva we approximate the same effect with a single
// destination-out group: draw a full-stage dim rect, then punch holes wherever
// a highlight rect lives. Simpler model, identical visual.

let scrim = null;
let punchGroup = null;
let active = null;
let activePunch = null;
let startX = 0;
let startY = 0;

function ensureScrim(engine) {
  // The previous engine's scrim/punchGroup may still be referenced by these
  // module-level vars even though their parent layer was destroyed when the
  // user closed the annotator. Re-test against the *current* engine's
  // contentLayer; if they're still attached, reuse — otherwise rebuild on
  // the new layer. Without this guard, the second open of the annotator
  // (and Vite HMR during dev) leaves the highlight tool with no scrim,
  // which breaks the dim-the-rest-of-the-page effect entirely.
  if (scrim && scrim.getLayer() === engine.contentLayer) return;

  scrim = new Konva.Rect({
    x: 0, y: 0,
    width: engine.layout.innerW,
    height: engine.layout.innerH,
    fill: 'rgba(0,0,0,0.3)',
    listening: false,
  });
  punchGroup = new Konva.Group({ listening: false });
  engine.contentLayer.add(scrim);
  engine.contentLayer.add(punchGroup);
  // Pin to the bottom of contentLayer so the scrim renders BEFORE any other
  // annotations (rectangles, arrows, etc.) on this layer, and the punch
  // (next sibling) can destination-out the scrim alpha on the same canvas.
  scrim.moveToBottom();
  punchGroup.zIndex(1);
}

export const highlight = {
  id: 'highlight',
  defaultProps: {},
  onPointerDown(engine) {
    const pos = pointerPos(engine);
    if (!engine.pointerInsideImage(pos)) return;
    ensureScrim(engine);
    startX = pos.x;
    startY = pos.y;
    active = new Konva.Rect({
      x: startX, y: startY,
      width: 0, height: 0,
      stroke: state.get('selectedColor'),
      strokeWidth: state.get('strokeWidth'),
      fill: 'transparent',
      cornerRadius: 4,
    });
    activePunch = new Konva.Rect({
      x: startX, y: startY,
      width: 0, height: 0,
      fill: 'black',
      globalCompositeOperation: 'destination-out',
    });
    engine.contentLayer.add(active);
    punchGroup.add(activePunch);
  },
  onPointerMove(engine) {
    if (!active) return;
    const pos = pointerPos(engine);
    const w = pos.x - startX;
    const h = pos.y - startY;
    const attrs = {
      x: w < 0 ? pos.x : startX,
      y: h < 0 ? pos.y : startY,
      width: Math.abs(w),
      height: Math.abs(h),
    };
    active.setAttrs(attrs);
    activePunch.setAttrs(attrs);
    engine.contentLayer.batchDraw();
  },
  onPointerUp(engine) {
    if (!active) return;
    // Capture in locals so the sync closure below doesn't read the module-
    // scope `active`/`activePunch` (which we null at end of this handler) on
    // future drag/transform events. Closures over `let` track the binding,
    // not the value, so without these locals every drag silently no-ops on
    // null after the next highlight is placed.
    const node = active;
    const punch = activePunch;
    active = null;
    activePunch = null;
    if (node.width() < 4 || node.height() < 4) {
      node.destroy();
      punch?.destroy();
      engine.contentLayer.batchDraw();
      return;
    }
    node.remove();
    attachInteractive(engine, node);
    // Bind the punch rect to the highlight rect's transform so they always
    // line up after drag/resize.
    const sync = () => {
      if (!punch) return;
      punch.setAttrs({
        x: node.x(), y: node.y(),
        width: node.width() * node.scaleX(),
        height: node.height() * node.scaleY(),
        rotation: node.rotation(),
      });
      engine.contentLayer.batchDraw();
    };
    node.on('dragmove.highlight transform.highlight', sync);
    // The engine reads `_companions` in deleteSelection to remove/restore
    // satellites alongside the owner. Konva's add/remove events don't fire
    // reliably for programmatic add/remove, so we hook into the engine's
    // delete path instead of listening for Konva lifecycle events.
    if (punch) node._companions = [punch];
    node.on('destroy.highlight', () => punch?.destroy());
    engine.contentLayer.batchDraw();
  },
  apply(node, prop, value) {
    if (prop === 'color') {
      node.stroke(value);
    } else if (prop === 'strokeWidth') {
      node.strokeWidth(value);
    }
  },
};
